import { useCallback, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';

import { PaymentClient } from 'v3/api/client/PaymentClient';
import { BankAccountLinkProps } from 'v3/hooks/bankAccountLink';
import { useQuery } from 'v3/hooks/useQuery';

const MICRODEPOSITS = 'verify_with_microdeposits';


/**
 * Link a bank account through Stripe Financial Connections.
 *
 * Replaces the Plaid bridge, which produced a legacy ACH source that stops working on
 * 2026-08-21. `useBankAccountLink` adapts Plaid's return shape onto this one, so the pages
 * speak in provider-neutral terms and neither hook has to lie about what it is.
 */
export const useStripeBankAccount = ({
  cfUuid,
  signContract,
  setIsLoading,
  setIsProcessing,
  onExit,
  billingDetails,
  shouldRedirect = true,
}: BankAccountLinkProps) => {
  const stripe = useStripe();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkError, setLinkError] = useState(false);

  const navigate = useNavigate();
  const query = useQuery();
  // Sign Deal carries ?name= and owns no redirect of its own, so the hook sends it onward.
  // Change Payment Method carries no name and its own submit callback navigates to
  // /change-payment-method/<cfUuid>/success — redirecting from here would overrule it, which
  // is exactly what a fallback name did. Absent name means "the caller handles this".
  const queryName = query.get('name');

  // The microdeposit page bounces without a name, and Change Payment Method has none to give,
  // so fall back to the account holder for that URL only.
  const microdepositName = encodeURIComponent(queryName ?? (billingDetails?.name ?? '').split(' ')[0]);

  const handleSuccess = useCallback(
    async (setupIntentId: string, destination?: string) => {
      setIsSubmitting(true);

      try {
        setIsProcessing('processing');
        await signContract({ setupIntentId });
        setIsProcessing('complete');

        if (destination && shouldRedirect) {
          navigate(destination);
        }
      } catch (error) {
        // Clearing isProcessing is what lets the customer try again: the CTA is disabled
        // while it reads 'processing', so leaving it set turns any failure into a dead end.
        setIsProcessing('');

        // signContract already reported this to the customer before throwing — re-reporting
        // here only stacks a second, vaguer toast on top of the real one.
        console.error('Bank account connection failed:', error);
      } finally {
        setIsSubmitting(false);
        setIsLoading(false);
      }
    },
    [signContract, shouldRedirect, navigate, setIsLoading, setIsProcessing]
  );

  const fail = useCallback((_message?: string) => {
    setLinkError(true);
    setIsSubmitting(false);
    setIsLoading(false);
    setIsProcessing('');
  }, [setIsLoading, setIsProcessing]);

  // The member closed the Financial Connections modal without choosing an account. Same
  // outcome as abandoning any link flow, so it reuses the page's existing exit handling.
  const abandon = useCallback(() => {
    setIsSubmitting(false);
    setIsLoading(false);
    setIsProcessing('');
    onExit?.();
  }, [onExit, setIsLoading, setIsProcessing]);

  const collect = useCallback(
    async (stripeClient: Stripe, clientSecret: string) => {
      const { setupIntent, error } = await stripeClient.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          // Stripe refuses to create an ACH payment method without an account holder name.
          payment_method_data: { billing_details: { name: billingDetails?.name ?? '', email: billingDetails?.email } },
        },
        expand: ['payment_method'],
      });

      if (error || !setupIntent) {
        return { error: error?.message ?? 'Could not read the selected bank account' };
      }

      // The member picked an account but has not agreed to the mandate yet.
      if (setupIntent.status === 'requires_confirmation') {
        return await stripeClient.confirmUsBankAccountSetup(clientSecret);
      }

      return { setupIntent };
    },
    [billingDetails]
  );

  const open = useCallback(async () => {
    if (!stripe) {
      fail('Stripe has not finished loading');
      return;
    }

    setIsSubmitting(true);
    setLinkError(false);

    const { data, error: setupError } = await PaymentClient().setupPaymentIntent(cfUuid, 'us_bank_account');
    const clientSecret = data?.clientSecret;

    if (setupError || !clientSecret) {
      fail('Could not start bank verification');
      return;
    }

    const result: any = await collect(stripe, clientSecret);

    if (result.error) {
      fail(typeof result.error === 'string' ? result.error : result.error.message);
      return;
    }

    const { setupIntent } = result;

    if (setupIntent.status === 'requires_payment_method') {
      abandon();
      return;
    }

    // Microdeposits take days. Submit the account anyway so the pending payment method is
    // recorded against the membership — the backend stores it unverified and the
    // `setup_intent.succeeded` webhook flips it.
    //
    // Stripe will not redirect back to us afterwards: `return_url` is documented as applying
    // only to redirect-based methods, and the API rejects it outright on an unconfirmed
    // SetupIntent. So we stay put and carry Stripe's hosted link onto our own page, which
    // leaves the member somewhere that belongs to us rather than stranded on Stripe's.
    const hostedVerificationUrl =
      setupIntent.next_action?.type === MICRODEPOSITS
        ? setupIntent.next_action.verify_with_microdeposits?.hosted_verification_url
        : undefined;

    if (hostedVerificationUrl) {
      const verification = encodeURIComponent(hostedVerificationUrl);
      await handleSuccess(setupIntent.id, `/microdeposits/?name=${microdepositName}&verifyUrl=${verification}`);
      return;
    }

    await handleSuccess(setupIntent.id, queryName ? `/payment-success/?name=${queryName}` : undefined);
  }, [stripe, cfUuid, collect, handleSuccess, fail, abandon, queryName, microdepositName]);

  return {
    open,
    ready: Boolean(stripe),
    isSubmitting,
    linkError,
  };
};
